import React from 'react';
import { apply } from 'json-logic-js';
import { Alignment, DynamicFieldsType } from '@philips-internal/oswald-ui-constants';
import { generateUUID, isEnumValid, propagateProps } from '@philips-internal/oswald-ui-utils';
import { Autocomplete } from '../autocomplete/autocomplete.js';
import { Checkbox } from '../checkbox/checkbox.js';
import { Checklist } from '../checklist/checklist.js';
import { DateField } from '../date-field/date-field.js';
import { Field } from '../field/field.js';
import { Fieldset } from '../fieldset/fieldset.js';
import { Input } from '../input/input.js';
import { Mapper } from '../mapper/mapper.js';
import { NotFound } from '../not-found/not-found.js';
import { RadioGroup } from '../radio-group/radio-group.js';
import { Text } from '../text/text.js';
import TextArea from '../text-area/text-area.js';
function jsonLogicEval(formula, data) {
    const expression = formula;
    if (!data) {
        return apply(expression);
    }
    return apply(expression, getFormulaValues(data));
}
function getInput(metadata) {
    const alignment = metadata?.alignment?.toLocaleLowerCase();
    return internalPropagateProps(React.createElement(Input, null), {
        ...metadata,
        alignment: isEnumValid(Alignment, alignment) ? alignment : Alignment.CENTER,
        enabled: !metadata.readOnly,
        formatters: metadata.formatters,
    });
}
function getCheckbox(metaData) {
    return internalPropagateProps(React.createElement(Checkbox, { name: metaData.id }), {
        ...metaData,
        enabled: !metaData.readOnly,
    });
}
function getDateField(metaData) {
    //@ts-ignore - ignoring because the required props will be passed through propagation
    return internalPropagateProps(React.createElement(DateField, { enabled: !metaData.readOnly }), metaData);
}
function getAutocomplete(metaData) {
    const { enabled, label, minSearchLength, options } = metaData;
    if (!options) {
        return null;
    }
    const map = {
        value: 'label',
    };
    const component = (React.createElement(Autocomplete, { data: options },
        React.createElement(Mapper, { map: map },
            React.createElement(Text, null))));
    return internalPropagateProps(component, {
        ...metaData,
        enabled: enabled,
        entry: 'label',
        minSearchLength: minSearchLength ? minSearchLength : 0,
        title: label,
        uuid: generateUUID(),
    });
}
function getRadioGroup(params) {
    const { metaData, onBlur, onFocus } = params;
    const { id, options, readOnly } = metaData;
    const map = {
        key: 'value',
        label: 'label',
        value: 'value',
    };
    const component = (React.createElement(Mapper, { data: options, map: map },
        React.createElement(RadioGroup, { enabled: !readOnly, name: id, onBlur: onBlur, onFocus: onFocus, readOnly: true, uuid: generateUUID() })));
    return component;
}
function getText(metaData) {
    return internalPropagateProps(React.createElement(Text, null), {
        ...metaData,
        alignment: metaData?.alignment?.toLocaleLowerCase() ?? Alignment.CENTER,
        size: metaData.fontSize,
        value: metaData.label,
    });
}
function getTextArea(metaData) {
    return internalPropagateProps(React.createElement(TextArea, null), {
        ...metaData,
        enabled: !metaData.readOnly,
        name: metaData.id,
    });
}
function getChecklist(metaData) {
    return internalPropagateProps(React.createElement(Checklist, { uuid: generateUUID() }), { ...metaData });
}
function hideLabel(child) {
    if (!child.fieldType) {
        return false;
    }
    return DynamicFieldsType.TEXT === child.fieldType;
}
function getLabel(child) {
    if (!child.label) {
        return '';
    }
    return child.fieldType === DynamicFieldsType.CHECKBOX ? '' : child.label;
}
function getValidators(child) {
    const required = {
        name: 'required',
    };
    return child.required ? [required] : [];
}
function getComponent(params) {
    const { child, addFormula, addVisualData, onBlur, onFocus } = params;
    const fieldType = child?.fieldType;
    if (!fieldType) {
        return null;
    }
    const components = {
        [DynamicFieldsType.AUTOCOMPLETE]: getAutocomplete(child),
        [DynamicFieldsType.CHECKLIST]: getChecklist(child),
        [DynamicFieldsType.CHECKBOX]: getCheckbox(child),
        [DynamicFieldsType.DATE_FIELD]: getDateField(child),
        [DynamicFieldsType.DYNAMIC_FIELDS]: null,
        [DynamicFieldsType.FIELD_SET]: null,
        [DynamicFieldsType.FIELD]: null,
        [DynamicFieldsType.INPUT]: getInput(child),
        [DynamicFieldsType.RADIO_GROUP]: getRadioGroup({ metaData: child, onBlur, onFocus }),
        [DynamicFieldsType.TEXT_AREA]: getTextArea(child),
        [DynamicFieldsType.TEXT]: getText(child),
    };
    //@ts-ignore - ignoring because the required props will be passed through propagation afterwards
    const component = components[fieldType] ?? React.createElement(Field, null);
    if (fieldType === DynamicFieldsType.TEXT && child.entry && child.label) {
        addVisualData(child.entry, child.label);
    }
    if (child.formula && child.entry) {
        addFormula(child.entry, child.formula);
    }
    return component;
}
function getField(params) {
    const { child, visible } = params;
    const { entry, id, width } = child;
    return (React.createElement(Field, { entry: String(entry), hideLabel: hideLabel(child), key: id, label: getLabel(child), sensitiveData: child.sensitiveData, uuid: generateUUID(), validators: getValidators(child), visible: visible, width: width ? Number(width) : 12 }, getComponent(params)));
}
function getFieldset(params) {
    const { children, id, label, data } = params;
    return (React.createElement(Fieldset, { key: id, section: { showCollapseButton: true, title: String(label) } }, children.map(child => {
        return getFieldOrFieldset({
            ...params,
            child,
            visible: resolveConditions(child, data),
        });
    })));
}
function internalPropagateProps(component, props) {
    if (props.children) {
        delete props.children;
    }
    // Casting to ReactElement here because in this case will always be an single element
    return propagateProps(component, { ...props });
}
function getFormulaValues(data) {
    const result = {};
    for (const key in data) {
        const value = typeof data[key] === 'object' ? data[key]['value'] : data[key];
        result[key] = parseFloat(value) || 0;
    }
    return result;
}
function getFieldOrFieldset(params) {
    const { addFormula, addVisualData, child, data, onBlur, onFocus, visible } = params;
    const { children, componentType, id, label } = child;
    if (children && componentType === DynamicFieldsType.FIELD_SET) {
        return getFieldset({
            addFormula,
            addVisualData,
            children,
            data,
            id,
            label,
            onBlur,
            onFocus,
        });
    }
    if (componentType === DynamicFieldsType.FIELD) {
        return getField({
            addFormula,
            addVisualData,
            child,
            onBlur,
            onFocus,
            visible,
        });
    }
    return (React.createElement(NotFound, null));
}
function resolveConditions(child, data) {
    if (!data || child.visibilityCondition === null || child.visibilityCondition === undefined) {
        return true;
    }
    return apply(child.visibilityCondition, data);
}
export { getFieldOrFieldset, jsonLogicEval, resolveConditions, };
//# sourceMappingURL=dynamic-fields-utils.js.map